import { useEffect, useMemo, useRef, useState } from 'react'
import { SectionHeader } from '../components/SectionHeader'
import { ProductCard } from '@/modules/products/ui/components/ProductCard'
import { useProducts } from '@/modules/products-api'
import type { ApiProduct } from '@/modules/products-api'
import type { Product } from '@/modules/products/model/types'

function toLegacy(p: ApiProduct): Product {
  return {
    id: p.id, slug: p.slug, name: p.name, categorySlug: p.categorySlug,
    brand: p.brand, price: p.price, oldPrice: p.oldPrice, discountPct: p.discountPct,
    image: p.image, images: p.images, rating: p.rating, reviews: p.reviews,
    inStock: p.inStock, shortDesc: p.shortDesc ?? undefined, specs: (p.specs ?? undefined),
  }
}

const PAGE_SIZE = 10

export function FeaturedProducts() {
  const [limit, setLimit] = useState(PAGE_SIZE)
  const { data, isLoading, isFetching } = useProducts({ limit, sort: 'popular' })
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const hasMore = items.length < total

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isFetching) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setLimit((l) => l + PAGE_SIZE)
        }
      },
      { rootMargin: '300px' },
    )
    io.observe(sentinelRef.current)
    return () => io.disconnect()
  }, [hasMore, isFetching, items.length])

  const skeletons = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => (
        <div key={`sk-${i}`} className="aspect-[3/4] bg-slate-100 rounded-xl animate-pulse" />
      )),
    [],
  )

  return (
    <section>
      <SectionHeader title="Sản Phẩm Nổi Bật" viewAllTo="/danh-muc/xe-may-dien" />
      {isLoading && items.length === 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">{skeletons}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {items.map((p) => (
              <ProductCard key={p.id} product={toLegacy(p)} />
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="h-10 mt-6 flex items-center justify-center">
              {isFetching && (
                <div className="flex items-center gap-2 text-sm text-muted">
                  <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  Đang tải thêm…
                </div>
              )}
            </div>
          )}

          {!hasMore && items.length > PAGE_SIZE && (
            <p className="text-center text-sm text-muted mt-6">Đã hiển thị tất cả sản phẩm</p>
          )}
        </>
      )}
    </section>
  )
}
